import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { GraduationCap, Menu, MessageCircleQuestion, X } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { CITIES } from "@/lib/api";

const LINKS = [
  { name: "Categories", href: "#categories", testid: "nav-link-categories" },
  { name: "Today's Answer", href: "#todays-answer", testid: "nav-link-todays-answer" },
  { name: "Browse 365", href: "#browse-questions", testid: "nav-link-browse" },
  { name: "About Frank", href: "#about-frank", testid: "nav-link-about" },
];

export default function Navbar({ onAskFrank }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const sectionHref = (hash) => {
    const cityPath = CITIES.find((c) => c.path === pathname)?.path ?? "/";
    return `${cityPath === "/" ? "" : cityPath}${hash}`;
  };
  return (
    <header className="sticky top-0 z-50">
      <div className="bg-brand-navy-dark text-white/80 text-[11px] sm:text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-1.5 flex items-center justify-center gap-2 sm:gap-3 font-medium tracking-wide">
          <span className="hidden sm:inline text-brand-gold">A Frank Powell family of sites</span>
          <span className="sm:hidden text-brand-gold">Frank Powell sites:</span>
          <span className="text-white/30">·</span>
          <a data-testid="ribbon-link-com" href="https://yourrealtorfrank.com" target="_blank" rel="noopener noreferrer" className="hover:text-brand-gold transition-colors">yourrealtorfrank.com</a>
          <span className="text-white/30">·</span>
          <a data-testid="ribbon-link-net" href="https://yourrealtorfrank.net" target="_blank" rel="noopener noreferrer" className="hover:text-brand-gold transition-colors">yourrealtorfrank.net</a>
          <span className="text-white/30">·</span>
          <a data-testid="ribbon-link-us" href="https://yourrealtorfrank.us" target="_blank" rel="noopener noreferrer" className="hover:text-brand-gold transition-colors">yourrealtorfrank.us<span className="hidden md:inline text-white/45"> — Moving &amp; Relocation</span></a>
          <span className="text-white/30">·</span>
          <a data-testid="ribbon-link-me" href="https://yourrealtorfrank.me" target="_blank" rel="noopener noreferrer" className="hover:text-brand-gold transition-colors">yourrealtorfrank.me<span className="hidden md:inline text-white/45"> — Meet Frank</span></a>
        </div>
      </div>
      <nav className="backdrop-blur-xl bg-brand-sand/90 border-b border-brand-border-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <BrandMark className="h-10 w-10 transition-transform duration-300 group-hover:-rotate-6" />
            <span className="leading-tight">
              <span className="block font-display font-black text-brand-navy-dark text-lg tracking-tight">YourRealtorFrank<span className="text-brand-red">.info</span></span>
              <span className="block text-[10px] uppercase tracking-[0.22em] text-brand-terracotta font-semibold">The Southeast Arizona 365 Almanac</span>
            </span>
          </Link>
          <div className="hidden lg:flex items-center gap-6 text-sm font-semibold text-brand-navy">
            {LINKS.map((l) => (
              <a key={l.name} data-testid={l.testid} href={sectionHref(l.href)} className="hover:text-brand-red transition-colors">{l.name}</a>
            ))}
            <Link data-testid="nav-link-blog" to="/blog" className={`transition-colors hover:text-brand-red ${pathname === "/blog" ? "text-brand-red" : ""}`}>Blog</Link>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <a data-testid="nav-pill-net" href="https://yourrealtorfrank.net" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border border-brand-navy/30 text-brand-navy text-xs font-semibold px-3.5 py-2 hover:border-brand-navy hover:bg-brand-navy hover:text-white transition-colors">
              <GraduationCap className="h-3.5 w-3.5" /> RE School
            </a>
            <button data-testid="nav-ask-frank-button" onClick={onAskFrank}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-red text-white text-xs font-bold px-4 py-2 hover:bg-brand-red-dark transition-colors">
              <MessageCircleQuestion className="h-3.5 w-3.5" /> Ask Frank
            </button>
          </div>
          <button data-testid="nav-mobile-toggle" className="lg:hidden text-brand-navy p-2" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        <div className="border-t border-brand-border-sand/70 bg-brand-sand-dark/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] overflow-x-auto">
            <span className="text-brand-slate/50 mr-1 shrink-0 normal-case tracking-normal font-medium">Almanacs:</span>
            {CITIES.map((c) => (
              <Link
                key={c.slug}
                data-testid={`nav-city-${c.slug}`}
                to={c.path}
                className={`shrink-0 rounded-full px-3 py-1 transition-colors ${
                  pathname === c.path
                    ? "bg-brand-red text-white"
                    : "text-brand-navy hover:bg-brand-navy hover:text-white"
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-brand-border-sand bg-brand-sand px-4 py-4 flex flex-col gap-3 text-sm font-semibold text-brand-navy">
            {LINKS.map((l) => (
              <a key={l.name} data-testid={`${l.testid}-mobile`} href={sectionHref(l.href)} onClick={() => setOpen(false)}>{l.name}</a>
            ))}
            <Link data-testid="nav-link-blog-mobile" to="/blog" onClick={() => setOpen(false)}>Blog</Link>
            <div className="pt-2 border-t border-brand-border-sand flex flex-wrap gap-2">
              {CITIES.map((c) => (
                <Link key={c.slug} data-testid={`nav-city-${c.slug}-mobile`} to={c.path} onClick={() => setOpen(false)}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-bold ${pathname === c.path ? "bg-brand-red text-white" : "bg-white border border-brand-border-sand"}`}>
                  {c.name}
                </Link>
              ))}
            </div>
            <button data-testid="nav-ask-frank-button-mobile" onClick={() => { setOpen(false); onAskFrank?.(); }}
              className="mt-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-red text-white text-sm font-bold px-4 py-2.5">
              <MessageCircleQuestion className="h-4 w-4" /> Ask Frank Directly
            </button>
          </div>
        )}
      </nav>
    </header>
  );
}
